import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../apiTools/apiClient';

// API endpoints
const API_BASE = process.env.REACT_APP_API_URL_RAW || 'http://localhost:5000';
const CREATE_MESSAGE = `${API_BASE}/api/messages`;
const GET_CONVERSATION = (userId, otherUserId) => `${API_BASE}/api/users/${userId}/conversations/${otherUserId}`;
const GET_USER_CONVERSATIONS = (userId) => `${API_BASE}/api/users/${userId}/conversations`;
const MARK_MESSAGE_READ = (messageId) => `${API_BASE}/api/messages/${messageId}/read`;
const MARK_CONVERSATION_READ = (userId, otherUserId) => `${API_BASE}/api/users/${userId}/conversations/${otherUserId}/read`;
const DELETE_MESSAGE = (messageId) => `${API_BASE}/api/messages/${messageId}`;
const DELETE_THREAD = (conversationId, threadId) => `${API_BASE}/api/conversations/${conversationId}/threads/${threadId}`;
const DELETE_CONVERSATION = (conversationId) => `${API_BASE}/api/conversations/${conversationId}`;

// Admin hard delete endpoints
const ADMIN_HARD_DELETE_MESSAGE = (messageId) => `${API_BASE}/api/admin/messages/${messageId}/hard`;
const ADMIN_HARD_DELETE_THREAD = (conversationId, threadId) => `${API_BASE}/api/admin/conversations/${conversationId}/threads/${threadId}/hard`;
const ADMIN_HARD_DELETE_CONVERSATION = (conversationId) => `${API_BASE}/api/admin/conversations/${conversationId}/hard`;

// Notification endpoints (unified into messaging system)
const GET_USER_NOTIFICATIONS = (userId) => `${API_BASE}/api/users/${userId}/notifications`;
const GET_UNREAD_NOTIFICATION_COUNT = (userId) => `${API_BASE}/api/users/${userId}/notifications/unread-count`;
const MARK_NOTIFICATION_READ = (notificationId) => `${API_BASE}/api/notifications/${notificationId}/read`;
const MARK_ALL_NOTIFICATIONS_READ = (userId) => `${API_BASE}/api/users/${userId}/notifications/read-all`;
const DELETE_NOTIFICATION = (notificationId) => `${API_BASE}/api/notifications/${notificationId}`;

// Async thunks
export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (messageData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(CREATE_MESSAGE, messageData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchConversation = createAsyncThunk(
  'messages/fetchConversation',
  async ({ userId, otherUserId, threadId, limit, offset, includeDeleted }, { rejectWithValue }) => {
    try {
      const queryParams = {};
      if (threadId) queryParams.threadId = threadId;
      if (limit !== undefined && limit !== null) queryParams.limit = parseInt(limit, 10);
      if (offset !== undefined && offset !== null) queryParams.offset = parseInt(offset, 10);
      if (includeDeleted) queryParams.includeDeleted = 'true';

      const response = await apiClient.get(GET_CONVERSATION(userId, otherUserId), {}, queryParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUserConversations = createAsyncThunk(
  'messages/fetchUserConversations',
  async (userId, { rejectWithValue }) => {
    try {
      const url = GET_USER_CONVERSATIONS(userId);
      console.log('[MSG_DEBUG] fetchUserConversations API call:', { userId, url });
      const response = await apiClient.get(url);
      console.log('[MSG_DEBUG] fetchUserConversations response:', {
        status: response.status,
        conversationCount: Array.isArray(response.data) ? response.data.length : 'not-array',
        data: response.data
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markMessageAsRead = createAsyncThunk(
  'messages/markMessageAsRead',
  async ({ messageId, userId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(MARK_MESSAGE_READ(messageId), { userId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markConversationAsRead = createAsyncThunk(
  'messages/markConversationAsRead',
  async ({ userId, otherUserId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(MARK_CONVERSATION_READ(userId, otherUserId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteMessage = createAsyncThunk(
  'messages/deleteMessage',
  async ({ messageId, userId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(DELETE_MESSAGE(messageId), {}, { data: { userId } });
      return { messageId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteThread = createAsyncThunk(
  'messages/deleteThread',
  async ({ conversationId, threadId, userId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(DELETE_THREAD(conversationId, threadId), {}, { data: { userId } });
      return { conversationId, threadId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteConversation = createAsyncThunk(
  'messages/deleteConversation',
  async ({ conversationId, userId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(DELETE_CONVERSATION(conversationId), {}, { data: { userId } });
      return { conversationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Admin-only hard delete async thunks
export const adminHardDeleteMessage = createAsyncThunk(
  'messages/adminHardDeleteMessage',
  async ({ messageId }, { rejectWithValue, getState }) => {
    try {
      // Check if user is admin
      const state = getState();
      const userRole = state.user.data?.role || state.user.data?.primaryRole;

      if (userRole !== 'admin') {
        return rejectWithValue('Admin privileges required for hard delete operations.');
      }

      const response = await apiClient.delete(ADMIN_HARD_DELETE_MESSAGE(messageId));
      return { messageId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adminHardDeleteThread = createAsyncThunk(
  'messages/adminHardDeleteThread',
  async ({ conversationId, threadId }, { rejectWithValue, getState }) => {
    try {
      // Check if user is admin
      const state = getState();
      const userRole = state.user.data?.role || state.user.data?.primaryRole;

      if (userRole !== 'admin') {
        return rejectWithValue('Admin privileges required for hard delete operations.');
      }

      const response = await apiClient.delete(ADMIN_HARD_DELETE_THREAD(conversationId, threadId));
      return { conversationId, threadId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const adminHardDeleteConversation = createAsyncThunk(
  'messages/adminHardDeleteConversation',
  async ({ conversationId }, { rejectWithValue, getState }) => {
    try {
      // Check if user is admin
      const state = getState();
      const userRole = state.user.data?.role || state.user.data?.primaryRole;

      if (userRole !== 'admin') {
        return rejectWithValue('Admin privileges required for hard delete operations.');
      }

      const response = await apiClient.delete(ADMIN_HARD_DELETE_CONVERSATION(conversationId));
      return { conversationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// ============================================================================
// NOTIFICATION THUNKS (unified into messaging system)
// ============================================================================

export const fetchNotifications = createAsyncThunk(
  'messages/fetchNotifications',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(GET_USER_NOTIFICATIONS(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchUnreadNotificationCount = createAsyncThunk(
  'messages/fetchUnreadNotificationCount',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(GET_UNREAD_NOTIFICATION_COUNT(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markNotificationRead = createAsyncThunk(
  'messages/markNotificationRead',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(MARK_NOTIFICATION_READ(notificationId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markAllNotificationsRead = createAsyncThunk(
  'messages/markAllNotificationsRead',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(MARK_ALL_NOTIFICATIONS_READ(userId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const removeNotification = createAsyncThunk(
  'messages/removeNotification',
  async (notificationId, { rejectWithValue }) => {
    try {
      const response = await apiClient.delete(DELETE_NOTIFICATION(notificationId));
      return { notificationId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  conversations: [],
  currentConversation: null,
  messages: {},
  activeThreads: {},
  unreadCounts: {},
  // Notification state (unified into messaging)
  notifications: [],
  notificationUnreadCount: 0,
  loading: {
    conversations: false,
    messages: false,
    sending: false,
    notifications: false
  },
  error: null
};

// Message slice
const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    // Real-time message handling
    addMessage: (state, action) => {
      const message = action.payload;
      const { conversationId } = message;

      if (!state.messages[conversationId]) {
        state.messages[conversationId] = [];
      }

      state.messages[conversationId].push(message);

      // Update conversation preview
      const conversationIndex = state.conversations.findIndex(
        conv => conv.conversationId === conversationId
      );

      if (conversationIndex !== -1) {
        state.conversations[conversationIndex].latestMessage = message;
        state.conversations[conversationIndex].lastMessageTime = message.createdAt;

        // Update unread count if message is for current user
        if (message.recipient.userId === state.currentUserId) {
          state.conversations[conversationIndex].unreadCount += 1;
        }
      }
    },

    // Set active conversation
    setActiveConversation: (state, action) => {
      state.currentConversation = action.payload;
    },

    // Set active thread
    setActiveThread: (state, action) => {
      const { conversationId, threadId } = action.payload;
      if (!state.activeThreads[conversationId]) {
        state.activeThreads[conversationId] = {};
      }
      state.activeThreads[conversationId].activeThread = threadId;
    },

    // Update message read status
    updateMessageReadStatus: (state, action) => {
      const { messageId, conversationId } = action.payload;
      const messages = state.messages[conversationId];

      if (messages) {
        const messageIndex = messages.findIndex(msg => msg.uuid === messageId);
        if (messageIndex !== -1) {
          messages[messageIndex].isRead = true;
          messages[messageIndex].readAt = new Date().toISOString();
        }
      }
    },

    // Update conversation unread count
    updateConversationUnreadCount: (state, action) => {
      const { conversationId, count } = action.payload;
      const conversationIndex = state.conversations.findIndex(
        conv => conv.conversationId === conversationId
      );

      if (conversationIndex !== -1) {
        state.conversations[conversationIndex].unreadCount = count;
      }
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset messages state
    resetMessages: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Send message
      .addCase(sendMessage.pending, (state) => {
        state.loading.sending = true;
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.loading.sending = false;
        const message = action.payload;
        const { conversationId } = message;

        if (!state.messages[conversationId]) {
          state.messages[conversationId] = [];
        }

        state.messages[conversationId].push(message);
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.loading.sending = false;
        state.error = action.payload;
      })

      // Fetch conversation
      .addCase(fetchConversation.pending, (state) => {
        state.loading.messages = true;
        state.error = null;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading.messages = false;
        const { conversationId, messages } = action.payload;
        state.messages[conversationId] = messages;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading.messages = false;
        state.error = action.payload;
      })

      // Fetch user conversations
      .addCase(fetchUserConversations.pending, (state) => {
        state.loading.conversations = true;
        state.error = null;
      })
      .addCase(fetchUserConversations.fulfilled, (state, action) => {
        state.loading.conversations = false;
        state.conversations = action.payload;
      })
      .addCase(fetchUserConversations.rejected, (state, action) => {
        state.loading.conversations = false;
        state.error = action.payload;
      })

      // Mark message as read
      .addCase(markMessageAsRead.fulfilled, (state, action) => {
        const message = action.payload;
        // Update message in state
        Object.keys(state.messages).forEach(conversationId => {
          const messageIndex = state.messages[conversationId].findIndex(
            msg => msg.uuid === message.uuid
          );
          if (messageIndex !== -1) {
            state.messages[conversationId][messageIndex] = message;
          }
        });
      })

      // Mark conversation as read
      .addCase(markConversationAsRead.fulfilled, (state, action) => {
        // Update unread counts in conversations
        const { count } = action.payload;
        // Implementation depends on which conversation was marked as read
      })

      // Delete message
      .addCase(deleteMessage.fulfilled, (state, action) => {
        const { messageId } = action.payload;
        // Mark message as deleted in all conversations
        Object.keys(state.messages).forEach(conversationId => {
          const messageIndex = state.messages[conversationId].findIndex(
            msg => msg.uuid === messageId
          );
          if (messageIndex !== -1) {
            state.messages[conversationId][messageIndex].isDeleted = true;
          }
        });
      })

      // Delete thread
      .addCase(deleteThread.fulfilled, (state, action) => {
        const { conversationId, threadId } = action.payload;
        // Mark all messages in the thread as deleted
        if (state.messages[conversationId]) {
          state.messages[conversationId].forEach(message => {
            if (message.threadId === threadId) {
              message.isDeleted = true;
            }
          });
        }
      })

      // Delete conversation
      .addCase(deleteConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        // Mark all messages in the conversation as deleted
        if (state.messages[conversationId]) {
          state.messages[conversationId].forEach(message => {
            message.isDeleted = true;
          });
        }
        // Remove conversation from conversations list
        state.conversations = state.conversations.filter(
          conv => conv.conversationId !== conversationId
        );
      })

      // Admin hard delete message
      .addCase(adminHardDeleteMessage.fulfilled, (state, action) => {
        const { messageId } = action.payload;
        // Completely remove message from all conversations
        Object.keys(state.messages).forEach(conversationId => {
          state.messages[conversationId] = state.messages[conversationId].filter(
            msg => msg.uuid !== messageId
          );
        });
      })
      .addCase(adminHardDeleteMessage.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Admin hard delete thread
      .addCase(adminHardDeleteThread.fulfilled, (state, action) => {
        const { conversationId, threadId } = action.payload;
        // Completely remove all messages in the thread
        if (state.messages[conversationId]) {
          state.messages[conversationId] = state.messages[conversationId].filter(
            message => message.threadId !== threadId
          );
        }
      })
      .addCase(adminHardDeleteThread.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Admin hard delete conversation
      .addCase(adminHardDeleteConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        // Completely remove all messages in the conversation
        delete state.messages[conversationId];
        // Remove conversation from conversations list
        state.conversations = state.conversations.filter(
          conv => conv.conversationId !== conversationId
        );
      })
      .addCase(adminHardDeleteConversation.rejected, (state, action) => {
        state.error = action.payload;
      })

      // ================================================================
      // NOTIFICATION REDUCERS
      // ================================================================

      // Fetch notifications
      .addCase(fetchNotifications.pending, (state) => {
        state.loading.notifications = true;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.notifications = Array.isArray(action.payload) ? action.payload : [];
        // Recalculate unread count from fetched data
        state.notificationUnreadCount = state.notifications.filter(n => !n.isRead).length;
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error = action.payload;
      })

      // Fetch unread notification count
      .addCase(fetchUnreadNotificationCount.fulfilled, (state, action) => {
        const count = action.payload?.count ?? action.payload?.unreadCount ?? action.payload ?? 0;
        state.notificationUnreadCount = typeof count === 'object' ? (count.low ?? 0) : count;
      })

      // Mark notification as read
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const notification = action.payload;
        const index = state.notifications.findIndex(n => n.uuid === notification.uuid);
        if (index !== -1) {
          state.notifications[index].isRead = true;
          state.notificationUnreadCount = Math.max(0, state.notificationUnreadCount - 1);
        }
      })

      // Mark all notifications as read
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.notifications.forEach(n => { n.isRead = true; });
        state.notificationUnreadCount = 0;
      })

      // Remove notification
      .addCase(removeNotification.fulfilled, (state, action) => {
        const { notificationId } = action.payload;
        const removed = state.notifications.find(n => n.uuid === notificationId);
        state.notifications = state.notifications.filter(n => n.uuid !== notificationId);
        if (removed && !removed.isRead) {
          state.notificationUnreadCount = Math.max(0, state.notificationUnreadCount - 1);
        }
      });
  }
});

// Actions
export const {
  addMessage,
  setActiveConversation,
  setActiveThread,
  updateMessageReadStatus,
  updateConversationUnreadCount,
  clearError,
  resetMessages
} = messageSlice.actions;

// Selectors
// Stable empty-array reference for "nothing yet" fallbacks. Returning a fresh
// `[]` per call makes a selector yield a new reference for identical state,
// which triggers React-Redux's "returned a different result when called with
// the same parameters" warning and causes needless rerenders.
const EMPTY_ARRAY = Object.freeze([]);

export const selectConversations = (state) => state.user.messages.conversations;
export const selectCurrentConversation = (state) => state.user.messages.currentConversation;
export const selectMessages = (conversationId) => (state) =>
  state.user.messages.messages[conversationId] || EMPTY_ARRAY;
export const selectActiveThread = (conversationId) => (state) =>
  state.user.messages.activeThreads[conversationId]?.activeThread;
export const selectMessagesLoading = (state) => state.user.messages.loading;
export const selectMessagesError = (state) => state.user.messages.error;
export const selectUnreadCount = (conversationId) => (state) => {
  const conversation = state.user.messages.conversations.find(
    conv => conv.conversationId === conversationId
  );
  return conversation?.unreadCount || 0;
};

// Notification selectors
export const selectNotifications = (state) => state.user.messages.notifications || EMPTY_ARRAY;
export const selectNotificationUnreadCount = (state) => state.user.messages.notificationUnreadCount || 0;
export const selectNotificationsLoading = (state) => state.user.messages.loading?.notifications || false;
export const selectCombinedUnreadCount = (state) => {
  const msgUnread = (state.user.messages.conversations || []).reduce(
    (sum, conv) => sum + (conv.unreadCount || 0), 0
  );
  const notifUnread = state.user.messages.notificationUnreadCount || 0;
  return msgUnread + notifUnread;
};

export default messageSlice.reducer;
